// Mirrors the reward calculation in ck-plus (reward-points.service.ts):
// in-network checkout orders earn 3%, out-of-network virtual card orders
// earn 1%, 1 point = $0.01, rounded up.
export const IN_NETWORK_REWARD_RATE = 0.03;
export const OUT_OF_NETWORK_REWARD_RATE = 0.01;

// PostHog feature flag gating the terms-page upsell card
export const PLUS_UPSELL_FEATURE_FLAG = 'ck-plus-upsell-card';

// ck-plus DEFAULT_PURCHASE_REWARD_MAX: 50,000 points per subscription
// year. Exact for this card's audience (trial-eligible = zero accrued
// points); if ck-plus's env override ever raises the cap, this becomes a
// safe under-promise until updated.
export const PURCHASE_REWARD_MAX_POINTS = 50000;

export const calculateRewardPoints = (
  amount,
  rate = IN_NETWORK_REWARD_RATE
) => {
  const parsed = parseFloat(amount);
  if (!parsed || parsed <= 0) return 0;

  return Math.min(Math.ceil(parsed * rate * 100), PURCHASE_REWARD_MAX_POINTS);
};

// Session types whose carts ck-web maps to IN_NETWORK (3%) reward orders
// (ShoppingCarts.determine_transaction_type -> Plus::Reward::Order).
const IN_NETWORK_SESSION_TYPES = [
  'checkout',
  'pi4_session',
  'pay_now',
  'consolidation',
  'instore',
];

// Merchants excluded from rewards in ck-web (Plus::Reward::Order)
const EXCLUDED_MERCHANT_SLUGS = ['melio'];

// Resolves the reward rate the current cart would actually earn, or null
// when it earns none:
// - standalone + virtual card -> virtual_card order -> 1%
// - instore + virtual card -> vcinstore -> no rewards
// - non-VC standalone -> CSR earns 3%, CKA earns nothing
//   (session.standalone_type; absent/unknown stays hidden)
export const rewardRateForOrder = (session, cart, merchant) => {
  if (EXCLUDED_MERCHANT_SLUGS.includes(merchant?.slug?.toLowerCase()))
    return null;

  const sessionType = session?.session_type;

  if (cart?.using_virtual_card) {
    return sessionType === 'standalone' ? OUT_OF_NETWORK_REWARD_RATE : null;
  }

  if (sessionType === 'standalone') {
    return session?.standalone_type === 'csr' ? IN_NETWORK_REWARD_RATE : null;
  }

  return IN_NETWORK_SESSION_TYPES.includes(sessionType)
    ? IN_NETWORK_REWARD_RATE
    : null;
};

// Plus opt-in intent, recorded on the terms page and consumed at order
// confirmation. The subscription itself is created right before the cart
// confirm request so it exists when ck-web's order_placed reward job runs.
const optInStorageKey = (cartId) => `ck_plus_opt_in_${cartId}`;

export const setPlusOptIn = (cartId, planName) => {
  try {
    sessionStorage.setItem(optInStorageKey(cartId), planName);
  } catch (e) {
    // Storage unavailable: the borrower can still join from the portal
  }
};

export const getPlusOptIn = (cartId) => {
  try {
    return sessionStorage.getItem(optInStorageKey(cartId));
  } catch (e) {
    return null;
  }
};

export const clearPlusOptIn = (cartId) => {
  try {
    sessionStorage.removeItem(optInStorageKey(cartId));
  } catch (e) {
    // Nothing to clean up if storage is unavailable
  }
};
